import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { mainButton } from '@tma.js/sdk-react'
import { useTranslation } from '../../i18n'
import { useTheme } from '../../hooks/useTheme'
import { userApi, type ApiError } from '../../api'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { Button } from '../ui'
import { captureEvent } from '../../posthog'

export default function CreateVirtualTrainee() {
  const navigate = useNavigate()
  const { t, translate } = useTranslation()
  const theme = useTheme()
  const { user } = useAuth()
  const entitlements = entitlementsOf(user)
  const canAddTrainee = entitlements.canAddTrainee
  const hasActiveCoach = entitlements.hasActiveCoach

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!canAddTrainee) {
      navigate(hasActiveCoach ? '/my-coaches' : '/subscription')
      return
    }

    if (!firstName.trim()) return

    setSaving(true)
    setError(null)

    try {
      await userApi.createVirtualTrainee({
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
      })
      captureEvent('virtual_trainee_created')

      if (mainButton.mount.isAvailable()) {
        mainButton.mount()
        mainButton.setText(`✅ ${t.virtualTrainee.button.success}`)
        mainButton.show()
        setTimeout(() => {
          mainButton.hide()
          navigate('/')
        }, 1500)
      } else {
        navigate('/')
      }
    } catch (err) {
      console.error('Error creating virtual trainee:', err)

      const apiError = err as ApiError
      let errorMessage = t.virtualTrainee.errors.unknown

      if (apiError.status === 0) {
        errorMessage = t.common.errors.networkError
      } else if (apiError.status === 400) {
        const errorMsg = apiError.message?.toLowerCase() || ''
        if (errorMsg.includes('notallowedwhencoached') || errorMsg.includes('addtrainee.notallowed')) {
          errorMessage = translate('subscription.upgradePrompt.leaveCoachToInvite')
        } else {
          errorMessage = t.virtualTrainee.errors.invalidData
        }
      } else if (apiError.status >= 500) {
        errorMessage = t.common.errors.serverError
      } else if (apiError.message) {
        errorMessage = apiError.message
      }

      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.virtualTrainee.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.virtualTrainee.subtitle}
        </p>
      </header>

      {hasActiveCoach && (
        <div
          className="mb-5 rounded-xl p-4 flex items-start gap-3"
          style={{ backgroundColor: `${theme.buttonColor}10`, border: `1px solid ${theme.buttonColor}25` }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: `${theme.buttonColor}20` }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4"/>
              <path d="M12 16h.01"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text.primary }}>
              {translate('subscription.upgradePrompt.leaveCoachTitle')}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
              {translate('subscription.upgradePrompt.leaveCoachToInvite')}
            </p>
          </div>
        </div>
      )}

      {hasActiveCoach && (
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => navigate('/my-coaches')}
        >
          {translate('subscription.upgradePrompt.leaveCoachCta')}
        </Button>
      )}

      {!hasActiveCoach && (
      <>
      <div
        className="rounded-xl mb-6 overflow-hidden"
        style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={() => setShowInfo(!showInfo)}
          className="w-full px-4 py-3.5 flex items-center justify-between active:opacity-75 transition-opacity"
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${theme.buttonColor}18` }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <span className="text-sm font-semibold" style={{ color: theme.text.primary }}>
              {t.virtualTrainee.info.title}
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              color: theme.text.muted,
              transform: showInfo ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0,
            }}
          >
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {showInfo && (
          <div
            className="px-4 pb-4 pt-1"
            style={{ borderTop: `1px solid ${theme.cardBorder}` }}
          >
            <p className="text-sm leading-relaxed mt-2" style={{ color: theme.text.muted }}>
              {t.virtualTrainee.info.description}
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && (
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-sm flex-1" style={{ color: theme.text.primary }}>{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-xl leading-none active:opacity-75 flex-shrink-0"
              style={{ color: theme.text.muted }}
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label htmlFor="firstName" className="text-sm font-medium" style={{ color: theme.text.primary }}>
            {t.virtualTrainee.form.firstNameRequired}
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value)
              setError(null)
            }}
            placeholder={t.virtualTrainee.form.firstNamePlaceholder}
            required
            minLength={2}
            maxLength={30}
            pattern="[\p{L}\p{M}\p{N}\s'-]+"
            disabled={saving}
            className="w-full rounded-xl px-4 py-3 text-base outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: theme.inputBg,
              color: theme.text.primary,
              border: `1px solid ${theme.inputBorder}`,
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="lastName" className="text-sm font-medium" style={{ color: theme.text.primary }}>
            {t.virtualTrainee.form.lastName}
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value)
              setError(null)
            }}
            placeholder={t.virtualTrainee.form.lastNamePlaceholder}
            maxLength={30}
            pattern="[\p{L}\p{M}\p{N}\s'-]*"
            disabled={saving}
            className="w-full rounded-xl px-4 py-3 text-base outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: theme.inputBg,
              color: theme.text.primary,
              border: `1px solid ${theme.inputBorder}`,
            }}
          />
          <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
            {t.virtualTrainee.form.lastNameHelp}
          </p>
        </div>

        <Button
          type="submit"
          disabled={!firstName.trim() || saving}
          variant="primary"
          size="md"
          fullWidth
        >
          {saving ? t.virtualTrainee.button.submitting : t.virtualTrainee.button.submit}
        </Button>
      </form>
      </>
      )}
    </div>
  )
}
